import { CancelButton, SaveButton } from '@core/components/Buttons';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import { Grid, Popover, Tooltip, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { cancelarAlert } from 'app/services/alerts';
import { centroCustoApi } from 'app/services/api/centroCustoApi';
import { FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import SelectGrupoCentroCusto from '../../../../components/Select/SelectGrupoCentroCusto';
import RateioContaPagarForm from './RateioContaPagarForm';

const PopoverRateioContaPagar = ({ index, anchorElIndex, anchorEl, onSubmit, onClose, categoria, disabled }) => {
  const [grupo, setGrupo] = useState({});
  const [somaPercentual, setSomaPercentual] = useState(0);
  const [grupoDisabled, setGrupoDisabled] = useState(false);

  const handleChangeGrupo = (event) => {
    setGrupo(event.target.value);

    if (event.target.value?.rateios) {
      let rateios = event.target.value.rateios?.map((rateio) => {
        return { classe: rateio.classe, percentual: rateio.percentual };
      });
      formik.handleChange({ target: { id: `rateios`, value: rateios } });
    }
  };

  const initialValues = {
    rateios: categoria.rateios || [
      {
        id: '',
        classe: {},
        percentual: '',
      },
    ],
  };

  const rateioSchema = Yup.object().shape({
    rateios: Yup.array().of(
      Yup.object().shape({
        classe: Yup.object()
          .nullable()
          .test('classe', mensagemCampoObrigatorio('o centro de custo.'), function(classe) {
            return !!classe?.id;
          }),
        percentual: Yup.number()
          .required(mensagemCampoObrigatorio('o percentual.'))
          .test('percentual', 'O somatório dos percentuais deve ser igual a 100%.', function(percentual) {
            return !(somaPercentual !== 100);
          }),
      })
    ),
  });

  const formik = useFormik({
    initialValues,
    onSubmit: onSubmit,
    validationSchema: rateioSchema,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const getSomaPercentual = (rateios) => {
    let valor = 0;
    if (rateios) {
      rateios.forEach((rateio) => {
        if (rateio.percentual) {
          valor += rateio.percentual;
        }
      });
    }
    return valor;
  };

  function isAtualizacaoRateios() {
    let possuiRateiosCadastrados = false;

    if (categoria?.rateios) {
      categoria.rateios.forEach((rateio) => {
        if (rateio.id) possuiRateiosCadastrados = true;
      });
    }
    return possuiRateiosCadastrados;
  }

  const categoriaClasseId = categoria?.classe?.id;
  useEffect(() => {
    const classeId = categoriaClasseId;

    if (classeId !== undefined) {
      const fetch = async () => {
        const [grupoCategoria, grupos] = await Promise.all([
          centroCustoApi().buscarGrupoPorCategoria(classeId),
          centroCustoApi().buscarGrupo(),
        ]);

        const possuiGrupoVinculado = grupoCategoria.data && Object.keys(grupoCategoria.data).length !== 0;
        const possuiGruposCadastrados = grupos.data && Object.keys(grupos.data).length > 0;
        const possuiRateiosCadastradosAnteriormente = isAtualizacaoRateios();

        const grupoAtual = possuiGrupoVinculado ? grupoCategoria.data : grupos.data;
        setGrupo(grupoAtual);

        if (possuiRateiosCadastradosAnteriormente) {
          formik.resetForm(initialValues);
        } else {
          formik.setFieldValue('rateios', grupoAtual?.rateios || [{}]);
        }

        setGrupoDisabled(!possuiGrupoVinculado && !possuiGruposCadastrados);
      };

      fetch();
    }
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriaClasseId]);

  useEffect(() => {
    const valorTotal = getSomaPercentual(formik.values.rateios);
    setSomaPercentual(valorTotal);
  }, [formik.values.rateios]);

  const handleCancelarClick = () => {
    if (formik.dirty) {
      cancelarAlert({
        confirmar: () => {
          formik.resetForm(initialValues);
          setGrupo({});
          onClose();
        },
      });
    } else {
      formik.resetForm(initialValues);
      setGrupo({});
      onClose();
    }
  };

  const PopoverHeader = () => (
    <>
      <Grid
        container
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 20,
        }}
      >
        <Grid item md={6} sm={6} xs={6}>
          <Typography
            style={{
              fontWeight: 'bold',
              color: 'rgb(35,61,77)',
              fontSize: 16,
            }}
          >
            Rateio dos centros de custos
          </Typography>
        </Grid>
        <Grid item md={6} sm={6} xs={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <CancelButton
            title="cancelar"
            type="button"
            onClick={() => {
              handleCancelarClick();
            }}
          >
            Cancelar
          </CancelButton>
          <SaveButton
            title="Salvar"
            type="button"
            onClick={() => {
              setGrupoDisabled(false);
              formik.handleSubmit();
            }}
          >
            Salvar
          </SaveButton>
        </Grid>
        <Grid item xs={12} md={12} sm={12} style={{ padding: 10 }}>
          <Alert severity="warning">
            Selecione um grupo existente ou informe os centros de custos desejados para rateio.
          </Alert>
        </Grid>
      </Grid>
    </>
  );

  const PopoverHeaderVisualizacao = () => (
    <>
      <Grid
        container
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: 20,
          paddingBottom: 20,
          paddingLeft: 20,
        }}
      >
        <Grid item md={8} sm={8} xs={8}>
          <Typography
            style={{
              fontWeight: 'bold',
              color: 'rgb(35,61,77)',
              fontSize: 16,
            }}
          >
            Rateio dos centros de custos
          </Typography>
        </Grid>
        <Grid item md={4} sm={4} xs={4} style={{ display: 'flex', justifyContent: 'center' }}>
          <CancelButton title="fechar" type="button" onClick={onClose}>
            Fechar
          </CancelButton>
        </Grid>
      </Grid>
      <Grid
        container
        style={{
          paddingTop: 15,
          paddingLeft: 20,
        }}
      >
        <Grid item md={6} sm={6} xs={6}>
          <Typography
            style={{
              fontWeight: 'bold',
            }}
          >
            Centro de custo
          </Typography>
        </Grid>
        <Grid item md={6} sm={6} xs={6}>
          <Typography
            style={{
              fontWeight: 'bold',
            }}
          >
            Percentual (%)
          </Typography>
        </Grid>
      </Grid>
    </>
  );

  function contemRateio() {
    let possuiRateiosCadastrados = false;

    if (categoria.rateios) {
      categoria.rateios.forEach((rateio) => {
        if (rateio.classe?.indice && rateio.classe?.nome) possuiRateiosCadastrados = true;
      });
    }
    return possuiRateiosCadastrados;
  }

  const PopoverContentVisualizacao = () => (
    <>
      {contemRateio() &&
        categoria.rateios?.map((rateio) => (
          <Grid container key={rateio.id} style={{ paddingLeft: 20 }}>
            <Grid container>
              <Grid item md={6} sm={6} xs={6}>
                <Grid style={{ marginTop: 5 }}>
                  <Typography>
                    {rateio.classe.indice} {rateio.classe.nome}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item md={6} sm={6} xs={6}>
                <Grid style={{ marginTop: 5 }}>
                  <Typography>{rateio.percentual}</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ))}
      <Grid style={{ paddingBottom: 20 }} />
    </>
  );

  return (
    <Popover
      open={index === anchorElIndex}
      anchorEl={anchorEl}
      onClose={handleCancelarClick}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        style: { width: 570 },
      }}
    >
      <FormikProvider value={formik}>
        {!disabled ? (
          <>
            <PopoverHeader />
            <Grid
              container
              spacing={2}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingLeft: 20,
                paddingBottom: 20,
                paddingRight: 20,
              }}
            >
              <Tooltip
                title="Não existe nenhum grupo de rateio cadastrado no sistema"
                disableHoverListener={!grupoDisabled}
                disableFocusListener={!grupoDisabled}
                disableTouchListener={!grupoDisabled}
              >
                <Grid item xs={12} sm={12} md={12} xl={12} lg={12}>
                  <SelectGrupoCentroCusto
                    id={grupo}
                    name={grupo}
                    label={'Grupos'}
                    value={grupo}
                    onChange={handleChangeGrupo}
                    onBlur={formik.handleBlur}
                    disabled={grupoDisabled}
                    tipoNatureza="DESPESA"
                  />
                </Grid>
              </Tooltip>
              <Grid item xs={12} sm={12} md={12} xl={12} lg={12}>
                <RateioContaPagarForm />
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <PopoverHeaderVisualizacao />
            <PopoverContentVisualizacao />
          </>
        )}
      </FormikProvider>
    </Popover>
  );
};

export default PopoverRateioContaPagar;
